import React from "react";
import { useSelector } from "react-redux";
import { selectQuestionEntities } from "features/question/questionSlice";
import { selectGameObject } from "features/gamestate/gameStateSlice";
import EditorCategoryCell from "./EditorCategoryCell";
import EditorValueCell from "./EditorValueCell";
import { selectPanelMode, selectSelectedQuestionId } from "./editorSlice";
import { isClueWritten } from "./clueWritten";
import "./roundEditor.css";

const ROW_INDICES = [0, 1, 2, 3, 4]; // 5 value rows

const EditorBoard = ({ round }) => {
  const gameObject = useSelector(selectGameObject);
  const questionEntities = useSelector(selectQuestionEntities);
  const panelMode = useSelector(selectPanelMode);
  const selectedQuestionId = useSelector(selectSelectedQuestionId);
  const hasSpotlight = panelMode === "clue" && !!selectedQuestionId;

  const cats = Array.isArray(gameObject?.categories?.[round])
    ? gameObject.categories[round]
    : [];
  const numCols = cats.length || 1;

  // The category map (game.categories[round][col]["1".."5"] = questionId) is the
  // authoritative column→clue mapping. Driving the grid off it keeps columns
  // aligned and lets empty / not-yet-loaded cells stay selectable for authoring.
  const questionAt = (colIdx, rowIdx) => {
    const qid = cats[colIdx]?.[String(rowIdx + 1)];
    return { qid, question: qid ? questionEntities?.[qid] : undefined };
  };

  const writtenInColumn = (colIdx) => {
    let n = 0;
    for (let row = 0; row < 5; row += 1) {
      if (isClueWritten(questionAt(colIdx, row).question)) n += 1;
    }
    return n;
  };

  return (
    <div
      className={`re-board ${hasSpotlight ? "has-spotlight" : ""}`}
      style={{ "--re-cols": numCols }}
    >
      {/* Category header row */}
      {cats.map((cat, colIdx) => (
        <EditorCategoryCell
          key={`cat-${colIdx}`}
          label={cat?.category || ""}
          categoryIdx={colIdx}
          round={round}
          clueCount={writtenInColumn(colIdx)}
        />
      ))}

      {/* Value rows */}
      {ROW_INDICES.map((rowIdx) =>
        cats.map((_, colIdx) => {
          const { qid, question } = questionAt(colIdx, rowIdx);
          return (
            <EditorValueCell
              key={`cell-${rowIdx}-${colIdx}`}
              questionId={qid}
              question={question}
              rowIndex={rowIdx}
              round={round}
            />
          );
        })
      )}

      <div
        className={`re-board-scrim ${hasSpotlight ? "active" : ""}`}
        aria-hidden="true"
      />
    </div>
  );
};

export default EditorBoard;
